// src/context/BrandThemeContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Brand } from "../types/BrandModels";
import { fetchBrands } from "../services/brandService";
import { useAuth } from "./AuthContext";
import {
  ThemeColors,
  DEFAULT_THEME_COLORS,
  extractThemeColors,
  updateCSSVariables,
} from "../utils/themeUtils";

interface BrandThemeContextType {
  selectedBrand: Brand | null;
  colors: ThemeColors;
  setCustomerId: (customerId: string | null) => void;
  isLoading: boolean;
}

const BrandThemeContext = createContext<BrandThemeContextType | undefined>(
  undefined
);

export const BrandThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { idToken } = useAuth();
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [customerId, setCustomerIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [colors, setColors] = useState<ThemeColors>(DEFAULT_THEME_COLORS);

  /**
   * Fetch brands and update theme when customer ID changes
   * NOTE: Currently using default Daisy AI colors only.
   * Brand-specific colors will be implemented in future.
   */
  const setCustomerId = useCallback(
    async (newCustomerId: string | null) => {
      setCustomerIdState(newCustomerId);

      // Always use default Daisy AI colors for now
      setSelectedBrand(null);
      setColors(DEFAULT_THEME_COLORS);
      updateCSSVariables(DEFAULT_THEME_COLORS);

      /* FUTURE: Enable brand-specific colors
      if (!newCustomerId || !idToken) {
        setSelectedBrand(null);
        setColors(DEFAULT_THEME_COLORS);
        updateCSSVariables(DEFAULT_THEME_COLORS);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetchBrands(newCustomerId, idToken);
        const brands = response?.brands || response || [];

        // Select the first brand if available
        if (brands.length > 0 && Array.isArray(brands)) {
          const brandData = brands[0];

          // Map API response to Brand type
          const brand: Brand = {
            id: brandData.id || brandData.brand_id,
            name: brandData.name || brandData.brand_name || "",
            colors: brandData.colors || [],
            logoUrl: brandData.logoUrl || brandData.logo_url || "",
            tagline: brandData.tagline || "",
            socialLinks: brandData.socialLinks || {},
            description: brandData.description || "",
            fonts: brandData.fonts || [],
            questionnaire_answers: brandData.questionnaire_answers,
            brand_website: brandData.brand_website,
            customer_id: newCustomerId,
          };

          setSelectedBrand(brand);

          // Extract and set theme colors
          const themeColors = extractThemeColors(brand.colors);
          setColors(themeColors);
          updateCSSVariables(themeColors);
        } else {
          // No brand found, use defaults
          setSelectedBrand(null);
          setColors(DEFAULT_THEME_COLORS);
          updateCSSVariables(DEFAULT_THEME_COLORS);
        }
      } catch (error) {
        debug.error("Error fetching brands for theme:", error);
        setSelectedBrand(null);
        setColors(DEFAULT_THEME_COLORS);
        updateCSSVariables(DEFAULT_THEME_COLORS);
      } finally {
        setIsLoading(false);
      }
      */
    },
    [idToken]
  );

  // Sync inline brand vars only when non-default (custom brand); defaults use `index.css`
  useEffect(() => {
    updateCSSVariables(colors);
  }, [colors]);

  return (
    <BrandThemeContext.Provider
      value={{
        selectedBrand,
        colors,
        setCustomerId,
        isLoading,
      }}
    >
      {children}
    </BrandThemeContext.Provider>
  );
};

export const useBrandTheme = () => {
  const context = useContext(BrandThemeContext);
  if (context === undefined) {
    throw new Error("useBrandTheme must be used within a BrandThemeProvider");
  }
  return context;
};
